import { callOpenAI } from './client';
import { generateConfigDescriptionPrompt, SYSTEM_PROMPTS } from './prompts';
import { extractJSON } from '../translations/utils';

export interface ConfigDescriptionResult {
  title: string;
  shortDescription: string;
  description: string;
  tokenUsage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

/**
 * Generates title, shortDescription, and description for a configuration
 * using OpenAI based on the configuration JSON
 */
export const generateConfigDescription = async (
  configJson: string | object
): Promise<ConfigDescriptionResult> => {
  // Convert config to JSON string if it's an object
  const configString =
    typeof configJson === 'string'
      ? configJson
      : JSON.stringify(configJson, null, 2);

  const prompt = generateConfigDescriptionPrompt(configString);

  try {
    const { content, usage } = await callOpenAI(
      [
        {
          role: 'system',
          content: SYSTEM_PROMPTS.CONFIG_DESCRIPTION_GENERATOR,
        },
        {
          role: 'user',
          content: prompt,
        },
      ],
      {
        model: 'gpt-4o-mini',
        temperature: 0.7, // Slightly higher for more creative descriptions
        maxTokens: 500, // Enough for title + short + long descriptions
      }
    );

    if (!content) {
      throw new Error('No response from OpenAI');
    }

    // Extract JSON from response
    const result = extractJSON(content);

    // Validate required fields
    if (!result.title || !result.shortDescription || !result.description) {
      throw new Error(
        'Invalid response format: missing title, shortDescription, or description'
      );
    }

    // Enforce character limits
    const title = result.title.substring(0, 50).trim();
    const shortDescription = result.shortDescription.substring(0, 150).trim();
    const description = result.description.substring(0, 300).trim();

    return {
      title,
      shortDescription,
      description,
      tokenUsage: usage,
    };
  } catch (error) {
    console.error('❌ Error generating configuration description:', error);
    throw error;
  }
};
